import { useContext, useEffect, useMemo, useState } from "react";
import SlideButton from "react-slide-button";
import { useTranslation } from "react-i18next";
import { MachineCenterContext } from "../../contexts/MachineCenterContext/MachineCenterContext";
import { HMIContext } from "../../contexts/HMIContext/HMIContext";
import { HMICachedDataContext } from "../../contexts/HMICachedDataContext/HMICachedDataContext";
import { isUptimeActive } from "../../utilities/storageUtilities";
import Box from "@mui/material/Box";
import DangerousOutlinedIcon from "@mui/icons-material/DangerousOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import { useStyles } from "./DowntimeButton.styles";
import { DOWNTIMEMETHODCAPTURE, FAILUREDETAILSSTEPS } from "../../constants/constants";
import useUptimeDowntimeActionsHMI from "hooks/useUptimeDowntimeActionsHMI";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { MQTTBrokerContext } from "contexts/MQTTBrokerContext/MQTTBrokerContext";

export const DowntimeButton = () => {
    const { t } = useTranslation();
    const { machineStatus, handleDowntimeCounter, plc, downtimeCounterBelowTrigger } = useContext(MachineCenterContext);
    const { failureDetailsStep } = useContext(HMIContext);
    const { store } = useContext(HMICachedDataContext);
    const { isBrokerConnected, isLocalServerActive } = useContext(MQTTBrokerContext);
    const { handleMachineInactiveDowntime } = useUptimeDowntimeActionsHMI();
    const [reset, setReset] = useState(0);
    const uptime = isUptimeActive();
    const classes = useStyles();

    useEffect(() => {
        if (machineStatus) {
            setReset((counter) => counter + 1);
            handleDowntimeCounter(false);
        }
    }, [machineStatus, handleDowntimeCounter]);

    const disableSlider = useMemo(
        () => !isBrokerConnected || !isLocalServerActive,
        [isBrokerConnected, isLocalServerActive],
    );

    return (
        <Box className={classes.downtimeButtonContainer}>
            {store.machineCenter?.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC && plc ? (
                uptime ? (
                    downtimeCounterBelowTrigger ? (
                        <Box className={`${classes.downtimeButtonPausedPLC} ${classes.overlayTextContainer}`}>
                            <PauseCircleOutlineIcon className={classes.overlayTextPausedOutlinedIconLeft} />
                            <span className={classes.mainTextTitle}>
                                {t("HMI.machine_status.paused").toUpperCase()}
                            </span>
                            <PauseCircleOutlineIcon className={classes.overlayTextPausedOutlinedIconRight} />
                        </Box>
                    ) : (
                        <Box className={`${classes.downtimeButtonPLC} ${classes.overlayTextContainer}`}>
                            <CheckCircleOutlineOutlinedIcon className={classes.overlayTextDangerousOutlinedIconLeft} />
                            <span className={classes.mainTextTitle}>
                                {t("HMI.machine_status.running").toUpperCase()}
                            </span>
                            <CheckCircleOutlineOutlinedIcon className={classes.overlayTextDangerousOutlinedIconRight} />
                        </Box>
                    )
                ) : (
                    <Box className={`${classes.overlayButtonPLC} ${classes.overlayTextContainer}`}>
                        {failureDetailsStep === FAILUREDETAILSSTEPS.DOWNTIMESAVED ||
                        failureDetailsStep === FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME ? (
                            <UpdateOutlinedIcon className={classes.overlayTextDangerousOutlinedIconLeft} />
                        ) : (
                            <DangerousOutlinedIcon className={classes.overlayTextDangerousOutlinedIconLeft} />
                        )}
                        <span className={classes.mainTextTitle}>
                            {t(
                                failureDetailsStep === FAILUREDETAILSSTEPS.DOWNTIMESAVED ||
                                    failureDetailsStep === FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME
                                    ? "HMI.machine_status.waiting_to_restart"
                                    : "HMI.machine_status.stopped",
                            ).toUpperCase()}
                        </span>
                        {failureDetailsStep === FAILUREDETAILSSTEPS.DOWNTIMESAVED ||
                        failureDetailsStep === FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME ? (
                            <UpdateOutlinedIcon className={classes.overlayTextDangerousOutlinedIconLeft} />
                        ) : (
                            <DangerousOutlinedIcon className={classes.overlayTextDangerousOutlinedIconLeft} />
                        )}
                    </Box>
                )
            ) : (
                <SlideButton
                    mainText={
                        <>
                            <span className={classes.mainTextTitle}>
                                {t("HMI.machine_status.running").toUpperCase()}
                            </span>
                            <Box className={classes.mainTitleIconIconContainer}>
                                <Box className={classes.mainTextTitleIconBox}>
                                    <DangerousOutlinedIcon className={classes.mainTextTitleIcon} />
                                </Box>
                            </Box>
                        </>
                    }
                    overlayText={
                        <Box className={classes.overlayTextContainer}>
                            <DangerousOutlinedIcon className={classes.overlayTextDangerousOutlinedIconLeft} />
                            <span className={classes.overlayTextTitle}>
                                {t("HMI.machine_status.stopped").toUpperCase()}
                            </span>
                            <DangerousOutlinedIcon className={classes.overlayTextDangerousOutlinedIconRight} />
                        </Box>
                    }
                    onSlideDone={handleMachineInactiveDowntime}
                    caret={<img src="/images/downtime_slide_button_icon.svg" alt="slide_button_icon" />}
                    classList={`${classes.downtimeButton} ${disableSlider ? classes.disabledButton : ""}`}
                    caretClassList={
                        machineStatus
                            ? `${classes.downtimeButtonCaret} ${disableSlider ? classes.disabledCaret : ""}`
                            : classes.visibilityHidden
                    }
                    overlayClassList={`${
                        !machineStatus ? classes.downtimeButtonOverlay : classes.downtimeButtonOverlayColor
                    } ${disableSlider ? classes.disabledOverlay : ""}`}
                    delta={2}
                    minSlideWidth={0.8}
                    minSlideVelocity={0.6}
                    customCaretWidth={72}
                    reset={reset}
                />
            )}
        </Box>
    );
};
